import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Shape del estado que el backend devuelve por GET /sincronizar-erp/status/{id}.
 * Es el mismo para los dos jobs (contratos y infraestructura) por diseño.
 */
export type SyncJobStatus = {
  job_id: string;
  tipo: string;
  stage: string;
  total: number;
  processed: number;
  percent: number;
  done: boolean;
  error?: string | null;
  mensajes?: string[];
  resumen?: Record<string, any> | null;
};

type Props = {
  open: boolean;
  titulo: string;
  status: SyncJobStatus | null;
  onClose: () => void;
};

/**
 * Modal con barra de progreso + estado textual para los jobs de sync.
 * Reutilizable entre los 2 botones de /catalogos (infra masivos y contratos
 * a EVA). El polling lo hace el caller; este componente solo pinta el estado
 * que recibe en `status`.
 */
export default function SyncProgressDialog({ open, titulo, status, onClose }: Props) {
  const done = !!status?.done;
  const tieneError = !!status?.error;
  const percent = status?.percent ?? 0;
  const isIndeterminate = !status || (!done && status.total === 0);

  return (
    <Dialog
      open={open}
      onClose={done ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!done}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            {titulo}
          </Typography>
          {done && !tieneError && (
            <Chip label="Completado" color="success" size="small" />
          )}
          {done && tieneError && (
            <Chip label="Falló" color="error" size="small" />
          )}
          {!done && (
            <Chip label="En curso" color="primary" size="small" variant="outlined" />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {status?.stage ?? 'Iniciando...'}
              </Typography>
              {!isIndeterminate && (
                <Typography variant="body2" fontWeight={600}>
                  {percent}%
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant={isIndeterminate ? 'indeterminate' : 'determinate'}
              value={percent}
              sx={{ height: 8, borderRadius: 1 }}
            />
            {!isIndeterminate && status && (
              <Typography variant="caption" color="text.secondary">
                {status.processed} de {status.total} procesados
              </Typography>
            )}
          </Box>

          {tieneError && (
            <Alert severity="error">
              <Typography variant="body2" fontWeight={600}>Error en la sincronizacion</Typography>
              <Typography variant="caption">{status?.error}</Typography>
            </Alert>
          )}

          {done && !tieneError && status?.resumen && (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Resumen
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(status.resumen)
                  .filter(([k]) => k !== 'errores' && k !== 'duracion_ms')
                  .map(([k, v]) => (
                    <Typography key={k} variant="caption" sx={{ display: 'block' }}>
                      <b>{k}</b>: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </Typography>
                  ))}
                {Array.isArray(status.resumen?.errores) && status.resumen.errores.length > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                    {status.resumen.errores.length} item(s) con error (ver mensajes)
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {status?.mensajes && status.mensajes.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Log de progreso
              </Typography>
              <List
                dense
                sx={{
                  maxHeight: 180,
                  overflow: 'auto',
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  fontFamily: 'monospace',
                }}
              >
                {status.mensajes.map((m, i) => (
                  <ListItem key={i} disableGutters sx={{ px: 1, py: 0 }}>
                    <ListItemText
                      primary={m}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!done} variant="contained">
          {done ? 'Cerrar' : 'Procesando...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
